import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

/**
 * Public product navigation — locked spec (Stage 1 · 2026-02).
 *
 * Signed-out order:  ColourMatch Home · ColourMatch Pro · How it works · Sign in · Start Visualising
 * Signed-in order :  ColourMatch Home · ColourMatch Pro · How it works · Projects · [Painter Dashboard] · [Admin] · Sign out · Start Visualising
 *
 * Mobile: hamburger opens a Sheet drawer exposing the same items.
 * Escape / close-button / backdrop / link-selection all close the drawer
 * (Radix Dialog primitive under Shadcn Sheet handles focus + Escape).
 *
 * "ColourMatch Pro" points at /painters/join (public repurposed landing).
 * Do not point it at /pro/* — those routes are authenticated.
 */
export default function TopNav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isSignedIn = !!user && user !== false;
  const [mobileOpen, setMobileOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `text-sm transition-colors ${isActive ? "text-brand-teal font-semibold" : "text-brand-navy/70 hover:text-brand-teal"}`;

  const mobileLinkClass = ({ isActive }) =>
    `w-full flex items-center py-3 px-3 text-base rounded-lg transition-colors ${
      isActive
        ? "text-brand-teal font-semibold bg-brand-teal/5"
        : "text-brand-ink hover:text-brand-teal hover:bg-brand-sand"
    }`;

  const handleSignOut = () => {
    logout();
    setMobileOpen(false);
    nav("/");
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-brand-border/60">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-home-link" className="flex items-center gap-2 tap-spring">
          <img
            src="/branding/primary-logo.png"
            alt="ColourMatch"
            className="h-14 w-auto"
            width="163"
            height="143"
            decoding="async"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" end className={linkClass} data-testid="nav-colourmatch-home">ColourMatch Home</NavLink>
          <NavLink to="/painters/join" className={linkClass} data-testid="nav-colourmatch-pro">ColourMatch Pro</NavLink>
          <NavLink to="/how-it-works" className={linkClass} data-testid="nav-how">How it works</NavLink>
          <NavLink to="/help" className={linkClass} data-testid="nav-help">Help</NavLink>

          {isSignedIn && (
            <NavLink to="/projects" className={linkClass} data-testid="nav-projects">Projects</NavLink>
          )}
          {isSignedIn && (
            <NavLink to="/colours" className={linkClass} data-testid="nav-colours">All Colours</NavLink>
          )}
          {isSignedIn && user.is_painter && (
            <NavLink to="/painter" className={linkClass} data-testid="nav-painter-dashboard">Painter Dashboard</NavLink>
          )}
          {isSignedIn && user.role === "admin" && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin">Admin</NavLink>
          )}

          {isSignedIn ? (
            <button
              onClick={() => { logout(); nav("/"); }}
              className="text-sm text-brand-navy/70 hover:text-brand-teal transition-colors"
              data-testid="nav-logout-btn"
            >
              Sign out
            </button>
          ) : (
            <Link to="/login" className="text-sm text-brand-navy/70 hover:text-brand-teal transition-colors" data-testid="nav-login">
              Sign in
            </Link>
          )}

          <Link
            to={isSignedIn ? "/visualiser/new" : "/register"}
            data-testid="nav-cta-start"
            className="bg-brand-navy text-white px-5 h-11 rounded-2xl flex items-center text-sm font-semibold hover:bg-brand-navy/90 hover:shadow-navy transition-all tap-spring"
          >
            Start Visualising
          </Link>
        </nav>

        {/* Mobile right-side: hamburger + Start CTA */}
        <div className="md:hidden flex items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Open navigation menu"
                data-testid="mobile-nav-trigger"
                className="w-10 h-10 rounded-xl border border-brand-border flex items-center justify-center text-brand-navy hover:bg-brand-sand transition-colors"
              >
                <Menu size={20} aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[86%] max-w-sm bg-white p-0 flex flex-col"
              data-testid="mobile-nav-drawer"
            >
              <SheetHeader className="px-5 pt-6 pb-4 border-b border-brand-border text-left">
                <SheetTitle className="text-brand-ink text-lg">Menu</SheetTitle>
                <SheetDescription className="sr-only">
                  ColourMatch navigation. Select a destination or use the close button.
                </SheetDescription>
              </SheetHeader>

              <nav className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-1" aria-label="Mobile navigation">
                <SheetClose asChild>
                  <NavLink to="/" end className={mobileLinkClass} data-testid="mobile-nav-colourmatch-home">
                    ColourMatch Home
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/painters/join" className={mobileLinkClass} data-testid="mobile-nav-colourmatch-pro">
                    ColourMatch Pro
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/how-it-works" className={mobileLinkClass} data-testid="mobile-nav-how">
                    How it works
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/help" className={mobileLinkClass} data-testid="mobile-nav-help">
                    Help &amp; Guides
                  </NavLink>
                </SheetClose>

                {isSignedIn && (
                  <SheetClose asChild>
                    <NavLink to="/projects" className={mobileLinkClass} data-testid="mobile-nav-projects">
                      Projects
                    </NavLink>
                  </SheetClose>
                )}
                {isSignedIn && user.is_painter && (
                  <SheetClose asChild>
                    <NavLink to="/painter" className={mobileLinkClass} data-testid="mobile-nav-painter-dashboard">
                      Painter Dashboard
                    </NavLink>
                  </SheetClose>
                )}
                {isSignedIn && user.role === "admin" && (
                  <SheetClose asChild>
                    <NavLink to="/admin" className={mobileLinkClass} data-testid="mobile-nav-admin">
                      Admin
                    </NavLink>
                  </SheetClose>
                )}
              </nav>

              <div className="px-5 py-4 border-t border-brand-border space-y-3">
                {isSignedIn ? (
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="w-full h-11 rounded-xl border border-brand-border text-brand-navy text-sm font-semibold hover:bg-brand-sand transition-colors"
                    data-testid="mobile-nav-logout-btn"
                  >
                    Sign out
                  </button>
                ) : (
                  <SheetClose asChild>
                    <Link
                      to="/login"
                      data-testid="mobile-nav-login"
                      className="w-full h-11 rounded-xl border border-brand-border text-brand-navy text-sm font-semibold flex items-center justify-center hover:bg-brand-sand transition-colors"
                    >
                      Sign in
                    </Link>
                  </SheetClose>
                )}
                <SheetClose asChild>
                  <Link
                    to={isSignedIn ? "/visualiser/new" : "/register"}
                    data-testid="mobile-nav-cta-start"
                    className="w-full h-12 rounded-2xl bg-brand-navy text-white text-sm font-semibold flex items-center justify-center hover:bg-brand-navy/90 transition-all"
                  >
                    Start Visualising
                  </Link>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>

          {/* Compact CTA outside the drawer for one-tap start */}
          <Link
            to={isSignedIn ? "/visualiser/new" : "/register"}
            data-testid="nav-cta-start-mobile"
            className="bg-brand-navy text-white px-4 h-10 rounded-xl flex items-center text-sm font-semibold tap-spring"
          >
            Start
          </Link>
        </div>
      </div>
    </header>
  );
}
